import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import { dom, tool } from "../../../fotastart";
import {
    pageInfo,
    scrollToTop,
    formatThousand,
    empty,
    inform,
    isMobile,
    cleanDispose,
    either,
    redirect,
    formatRating,
    monetary,
    clearSearchSuggestion,
} from "../../../helpers";
import { PAGES, api, routes, STRINGS, gc, dc } from "../../../declarations";
import SearchResultProductView from "../../Views/SearchResultProductView";
import OffcanvasBody from "../../Offcanvas/OffcanvasBody";
import ModalBody from "../../Modals/ModalBody";

const h = app("h");
const broker = app("broker");

const formatStatsWithPlus = (val) => {
    let count = Number(val) || 0;
    if (count <= 0) return "0";
    return `${formatThousand(count)}+`;
};

export default class BusinessProfileDefault extends Component {
    businessId = null;
    companyState = state(null);
    categoriesState = state(null);
    selectedCategoryState = state(null);
    subcategoriesState = state(null);
    featuredProductsState = state([]);
    topSellersState = state([]);
    activeDealsState = state([]);
    loading = state(true);
    categoryLoading = state(true);
    infoHolder = null;

    constructor() {
        super();
        this.companyState.listener(this);
        this.categoriesState.listener(this);
        this.selectedCategoryState.listener(this);
        this.subcategoriesState.listener(this);
        this.featuredProductsState.listener(this);
        this.topSellersState.listener(this);
        this.activeDealsState.listener(this);
        this.loading.listener(this);
        this.categoryLoading.listener(this);
    }

    async init() {
        clearSearchSuggestion();
        
        this.businessId = app("router").params.businessId;
        this.loading.value = true;
        try {
            // Fetch the business profile
            let res = await api.get(`/companies/${this.businessId}`);
            if (res && res.data && res.data.company) {
                this.companyState.value = res.data.company;
                pageInfo(res.data.company.name + " Profile");
            }

            // Fetch root categories
            let catRes = await api.get(`/companies/${this.businessId}/categories/top`);
            if (catRes && catRes.data && catRes.data.categories) {
                let cats = catRes.data.categories;
                this.categoriesState.value = cats;

                if (cats.length > 0) {
                    this.selectCategory(cats[0].id);
                }
                else {
                    this.categoryLoading.value = false;
                }

            }

            // Featured products & top sellers power their own carousels
            // further down the page - fetched alongside everything else
            // here, each independently so one failing doesn't blank out
            // the other.
            this.loadFeaturedProducts();
            this.loadTopSellers();
            this.loadActiveDeals();
        } catch (error) {
            console.error(error);
            inform("Failed to load business profile.", STRINGS.ERROR);
        } finally {
            this.loading.value = false;
        }
    }

    async loadFeaturedProducts() {
        try {
            let res = await api.get(
                `/companies/${this.businessId}/products/featured`,
                { interval: 10 },
                { silent: true },
            );
            if (res && res.data && res.data.products) {
                this.featuredProductsState.value = res.data.products.data || [];
            }
        } catch (error) {
            console.error(error);
        }
    }

    async loadTopSellers() {
        try {
            let res = await api.get(
                `/companies/${this.businessId}/products/top-sellers`,
                { interval: 10 },
                { silent: true },
            );
            if (res && res.data && res.data.products) {
                this.topSellersState.value = res.data.products.data || [];
            }
        } catch (error) {
            console.error(error);
        }
    }

    async loadActiveDeals() {
        try {
            let res = await api.get(
                `/companies/${this.businessId}/deals/active`,
                {},
                { silent: true },
            );
            if (res && res.data && res.data.deals) {
                this.activeDealsState.value = res.data.deals || [];
            }
        } catch (error) {
            console.error(error);
        }
    }

    async selectCategory(categoryId) {
        this.selectedCategoryState.value = categoryId;
        this.subcategoriesState.value = []; // Reset while loading
        this.categoryLoading.value = true;
        try {
            let res = await api.get(`/companies/${this.businessId}/categories/${categoryId}/subcategories-with-products`);
            if (res && res.data && res.data.subcategories) {
                this.subcategoriesState.value = res.data.subcategories;
            }
        } catch (error) {
            console.error(error);
            inform("Failed to load subcategories.", STRINGS.ERROR);
        } finally {
            this.categoryLoading.value = false;
        }
    }

    async toggleBookmark(event) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        const company = this.companyState.value;
        if (!company) return;

        const bookmarked = company.currently_bookmarked;
        const url = `/company-bookmarks/${company.id}`;

        try {
            let res;
            if (bookmarked) {
                res = await api.delete(url);
                if (res) {
                    inform(res.message || "Bookmark removed", STRINGS.SUCCESS);
                    this.companyState.value = {
                        ...company,
                        currently_bookmarked: false,
                        save_count: Math.max(0, (company.save_count || 0) - 1),
                    };
                }
            } else {
                res = await api.post(url);
                if (res) {
                    inform(res.message || "Business bookmarked", STRINGS.SUCCESS);
                    this.companyState.value = {
                        ...company,
                        currently_bookmarked: true,
                        save_count: (company.save_count || 0) + 1,
                    };
                }
            }
        } catch (e) {
            inform(e.response?.data?.message || "Failed to update bookmark", STRINGS.ERROR);
        }
    }

    openInfoPopup(e) {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        const company = this.companyState.value;
        if (!company) return;

        if (this.infoHolder) {
            cleanDispose(this.infoHolder);
        }

        const details = () => this.renderInfoDetails(company);

        if (isMobile()) {
            this.infoHolder = tool.offcanvas(
                h.OffcanvasBody(details()),
                {
                    class: "rounded-top-5 shadow-lg",
                    style: "height: auto; max-height: 80vh;"
                }
            );
        } else {
            this.infoHolder = tool.plainModal(
                h.ModalBody(details())
            );
        }

        this.infoHolder.show();
    }

    renderInfoDetails(company) {
        let whatsappLink = company.support_phone ? `https://api.whatsapp.com/send?phone=${company.support_phone.replace(/[^0-9]/g, "")}` : null;
        let displayLocation = company.location;
        if (!displayLocation && company.branches && company.branches.length > 0) {
            displayLocation = company.branches[0].location?.readable_address;
        }

        return h.div(
            { class: "p-3" },
            // Header
            h.div(
                { class: "d-flex justify-content-between align-items-center mb-4" },
                h.h4({ class: "fw-bold mb-0 text-dark" }, "Business Details"),
                h.button({
                    type: "button",
                    class: "btn-close",
                    listeners: {
                        click: () => this.infoHolder?.hide()
                    }
                })
            ),
            
            // Description
            company.description ? h.div(
                { class: "mb-4" },
                h.h6({ class: "fw-bold text-muted text-uppercase small mb-2" }, "About Us"),
                h.p({ class: "text-secondary mb-0", style: "line-height: 1.6;" }, company.description)
            ) : null,

            // Contact Us Section
            h.div(
                { class: "mb-4" },
                h.h6({ class: "fw-bold text-muted text-uppercase small mb-3" }, "Contact Information"),
                h.div(
                    { class: "d-grid gap-2" },
                    company.support_phone ? h.a(
                        {
                            class: "btn btn-light text-start border p-3 rounded-3 d-flex align-items-center gap-3",
                            href: `tel:${company.support_phone}`,
                            target: "_blank"
                        },
                        h.i({ class: "bi bi-telephone text-primary fs-5" }),
                        h.div(
                            h.div({ class: "text-muted small" }, "Phone Support"),
                            h.div({ class: "fw-semibold text-dark" }, company.support_phone)
                        )
                    ) : null,
                    whatsappLink ? h.a(
                        {
                            class: "btn btn-light text-start border p-3 rounded-3 d-flex align-items-center gap-3",
                            href: whatsappLink,
                            target: "_blank"
                        },
                        h.i({ class: "bi bi-whatsapp text-success fs-5" }),
                        h.div(
                            h.div({ class: "text-muted small" }, "WhatsApp Chat"),
                            h.div({ class: "fw-semibold text-dark" }, "Chat with us")
                        )
                    ) : null,
                    company.support_email ? h.a(
                        {
                            class: "btn btn-light text-start border p-3 rounded-3 d-flex align-items-center gap-3",
                            href: `mailto:${company.support_email}`,
                            target: "_blank"
                        },
                        h.i({ class: "bi bi-envelope text-info fs-5" }),
                        h.div(
                            h.div({ class: "text-muted small" }, "Email Support"),
                            h.div({ class: "fw-semibold text-dark" }, company.support_email)
                        )
                    ) : null,
                    company.support_link ? h.a(
                        {
                            class: "btn btn-light text-start border p-3 rounded-3 d-flex align-items-center gap-3",
                            href: company.support_link,
                            target: "_blank"
                        },
                        h.i({ class: "bi bi-globe text-secondary fs-5" }),
                        h.div(
                            h.div({ class: "text-muted small" }, "Website / Support Page"),
                            h.div({ class: "fw-semibold text-dark" }, "Visit Website")
                        )
                    ) : null
                )
            ),

            // Additional Info (Legal ID, Location Address)
            (company.legal_id_number || displayLocation) ? h.div(
                { class: "border-top pt-4" },
                h.h6({ class: "fw-bold text-muted text-uppercase small mb-3" }, "More Information"),
                h.div(
                    { class: "d-grid gap-3" },
                    company.legal_id_number ? h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.div({ class: "bg-light text-secondary rounded p-2 d-flex align-items-center justify-content-center", style: "width: 40px; height: 40px;" }, h.i({ class: "bi bi-file-earmark-text fs-5" })),
                        h.div(
                            h.div({ class: "text-muted small" }, "Legal Registration Number"),
                            h.div({ class: "fw-semibold text-dark" }, company.legal_id_number)
                        )
                    ) : null,
                    displayLocation ? h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.div({ class: "bg-light text-secondary rounded p-2 d-flex align-items-center justify-content-center", style: "width: 40px; height: 40px;" }, h.i({ class: "bi bi-geo-alt fs-5" })),
                        h.div(
                            h.div({ class: "text-muted small" }, "Business Address"),
                            h.div({ class: "fw-semibold text-dark" }, displayLocation)
                        )
                    ) : null
                )
            ) : null
        );
    }

    cleanUp() {
        super.cleanUp();
        if (this.infoHolder) {
            cleanDispose(this.infoHolder);
        }
    }

    /**
     * Shared markup for the "row of this business's products the user can
     * scroll through, with a View All that goes to the same generic
     * results page" sections (Featured Products, Top Sellers) - same
     * horizontal-scroll card-row pattern already used for the subcategory
     * rows further down this page, just with a route-based "View all"
     * instead of a subcategory id.
     */
    renderProductCarousel(title, products, viewAllRouteName) {
        return h.div(
            { class: "mb-5" },
            h.div(
                { class: "d-flex justify-content-between align-items-center mb-3" },
                h.h5({ class: "fw-bold text-dark m-0" }, title),
                h.span(
                    {
                        class: "btn btn-sm btn-light fw-bold text-primary",
                        role: "button",
                        listeners: {
                            click: () => {
                                app("router").to(viewAllRouteName, {
                                    businessId: this.businessId,
                                });
                            },
                        },
                    },
                    "View all ",
                    h.i({ class: "bi bi-chevron-right" }),
                ),
            ),
            h.div(
                { class: "d-flex overflow-auto flex-nowrap gap-3 pb-3 desktop-scrollbar", style: "scrollbar-width: none;" },
                ...products.slice(0, 10).map(product => h.SearchResultProductView(product, {
                    class: "flex-shrink-0 h-100",
                    style: isMobile() ? "width: 150px;" : "width: 280px;",
                    listeners: {
                        click: () => {
                            broker.send(
                                app("$e").product.needs.display,
                                payload({ product }),
                            );
                        }
                    }
                }))
            ),
        );
    }

    /**
     * Same horizontal-scroll card-row shell as renderProductCarousel, but
     * for the business's currently-active deals rather than its products -
     * each card links to the deal-products listing page (BusinessProductListingPage
     * in "business.products.deal" mode) instead of opening a product.
     */
    renderDealsCarousel(deals) {
        return h.div(
            { class: "mb-5" },
            h.div(
                { class: "d-flex justify-content-between align-items-center mb-3" },
                h.h5({ class: "fw-bold text-dark m-0" }, "Active Deals"),
            ),
            h.div(
                { class: "d-flex overflow-auto flex-nowrap gap-3 pb-3 desktop-scrollbar", style: "scrollbar-width: none;" },
                ...deals.slice(0, 10).map(deal => this.renderDealCard(deal)),
            ),
        );
    }

    renderDealCard(deal) {
        let discountLabel = deal.type === "percentage"
            ? `-${deal.value}%`
            : `-${monetary(deal.value, dc.userCurrency)}`;
        let productCount = deal.product_count || 0;

        return h.div(
            {
                class: "flex-shrink-0 bg-white rounded-4 shadow-sm border border-light overflow-hidden position-relative",
                style: isMobile() ? "width: 160px;" : "width: 220px;",
                role: "button",
                listeners: {
                    click: () => {
                        app("router").to("business.products.deal", {
                            businessId: this.businessId,
                            dealId: deal.id,
                        });
                    },
                },
            },
            deal.thumbnail
                ? h.div({
                    class: "w-100",
                    style: "height: 100px; background: url('" + deal.thumbnail + "') no-repeat center center; background-size: cover;",
                })
                : h.div(
                    {
                        class: "w-100 d-flex align-items-center justify-content-center",
                        style: "height: 100px; background: linear-gradient(135deg, #ff512f 0%, #dd2476 100%);",
                    },
                    h.i({ class: "bi bi-tags-fill text-white", style: "font-size: 1.8rem;" }),
                ),
            h.span(
                {
                    class: "position-absolute top-0 start-0 m-2 badge bg-danger rounded-pill px-2 py-1 fw-bold d-inline-flex align-items-center gap-1",
                    style: "font-size: 0.7rem;",
                },
                h.i({ class: "bi bi-tag-fill" }),
                discountLabel,
            ),
            h.div(
                { class: "p-2" },
                h.div(
                    { class: "fw-bold text-dark text-truncate", style: "font-size: 0.85rem;" },
                    deal.name,
                ),
                h.div(
                    { class: "text-muted small" },
                    `${productCount} product${productCount === 1 ? "" : "s"}`,
                ),
            ),
        );
    }

    render() {
        if (this.businessId !== app("router").params.businessId) {
            scrollToTop();
            this.init();
        }

        return h.div(
            { class: "bg-light pb-5 py-md-5", style: "min-height: 100vh;" },
            v(this.loading, (loadingState) => {
                let isLoading = loadingState.value;
                if (isLoading) {
                    return h.div(
                        { class: "d-flex justify-content-center align-items-center w-100", style: "height: 60vh;" },
                        h.div({ class: "spinner-border text-primary", role: "status" })
                    );
                }

                return v(this.companyState, (companyState) => {
                    let company = companyState.value;
                    if (!company) {
                        return h.div(
                            { class: "d-flex justify-content-center align-items-center w-100 text-secondary flex-column", style: "height: 60vh;" },
                            h.i({ class: "bi bi-shop-window mb-3", style: "font-size: 3rem;" }),
                            h.h4("Business not found")
                        );
                    }

                    let initial = company.name ? company.name.charAt(0).toUpperCase() : "?";
                    let isVerified = company.is_approved;
                    let openStatus = company.branches_open_status;

                    return h.div(
                        // 1. The Decision Layer (Header)
                        h.div(
                            { class: "container pt-4 px-md-4 pb-0" },
                            h.div(
                                { class: "row g-4" },

                                // Left Column (The card/cart of the shop with cover, logo, name, bookmark/info icons)
                                h.div(
                                    { class: "col-12 col-md-6 p-0" },
                                    h.div(
                                        { class: "position-relative bg-white shadow-sm rounded-4 overflow-hidden border border-light" },

                                        // Absolute Positioned Open Status Badge on Top-Left
                                        h.div(
                                            { class: "position-absolute top-0 start-0 p-3", style: "z-index: 10;" },
                                            h.span(
                                                {
                                                    class: "badge shadow-sm d-flex px-2 py-1 rounded-pill fw-bold d-inline-flex align-items-center gap-1",
                                                    style: "font-size: 0.75rem; background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(4px);"
                                                },
                                                h.call(() => {
                                                    if (openStatus == "all") {
                                                        return [
                                                            h.i({
                                                                class: "bi bi-circle-fill text-success",
                                                                style: "font-size: 0.5rem;",
                                                            }),
                                                            h.span(
                                                                { class: "text-success" },
                                                                "Open Now",
                                                            ),
                                                        ];
                                                    }

                                                    if (openStatus == "some") {
                                                        return [
                                                            h.i({
                                                                class: "bi bi-circle-half text-blue",
                                                                style: "font-size: 0.5rem;",
                                                            }),
                                                            h.span(
                                                                { class: "text-blue" },
                                                                "Open Outlets",
                                                            ),
                                                        ];
                                                    }

                                                    return [
                                                        h.i({
                                                            class: "bi bi-circle text-dark",
                                                            style: "font-size: 0.5rem;",
                                                        }),
                                                        h.span(
                                                            { class: "text-dark" },
                                                            "Closed",
                                                        ),
                                                    ];
                                                })
                                            )
                                        ),

                                        // Absolute Positioned Bookmark and Info Icons on Top-Right
                                        h.div(
                                            { class: "position-absolute top-0 end-0 p-3 d-flex gap-2", style: "z-index: 10;" },
                                            // Bookmark Button
                                            h.button(
                                                {
                                                    class: "btn btn-light rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center border-0",
                                                    style: "width: 35px; height: 35px; background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(4px);",
                                                    listeners: {
                                                        click: (e) => this.toggleBookmark(e)
                                                    }
                                                },
                                                h.i({
                                                    class: company.currently_bookmarked
                                                        ? "bi bi-bookmark-fill text-primary"
                                                        : "bi bi-bookmark text-secondary",
                                                    style: "font-size: 1.1rem;"
                                                })
                                            ),
                                            // Info Button
                                            h.button(
                                                {
                                                    class: "btn btn-light rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center border-0",
                                                    style: "width: 35px; height: 35px; background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(4px);",
                                                    listeners: {
                                                        click: (e) => this.openInfoPopup(e)
                                                    }
                                                },
                                                h.i({
                                                    class: "bi bi-info-circle text-secondary",
                                                    style: "font-size: 1.1rem;"
                                                })
                                            )
                                        ),

                                        // Cover Image (16:9 Aspect Ratio)
                                        company.cover_path
                                            ? h.div({
                                                class: "w-100 position-relative",
                                                style: "padding-top: 56.25%; background: url('" + company.cover_path + "') no-repeat center center; background-size: cover;"
                                            })
                                            : h.div({
                                                class: "w-100 position-relative overflow-hidden",
                                                style: "padding-top: 56.25%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);"
                                            },
                                            h.div({
                                                class: "position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                            },
                                            h.span({
                                                class: "text-white fw-bold opacity-25 user-select-none",
                                                style: "font-size: 15vw; transform: rotate(-15deg); line-height: 1;"
                                            }, initial)
                                            )
                                        ),

                                        // Info Container
                                        h.div(
                                            { class: "container position-relative px-4 pb-3" },
                                            h.div(
                                                { class: "d-flex align-items-end gap-3", style: "margin-top: -30px;" },

                                                // Logo (Left, Square, Small)
                                                h.div(
                                                    { class: "position-relative flex-shrink-0" },
                                                    company.logo_path
                                                        ? h.img({
                                                            src: company.logo_path,
                                                            class: "rounded-3 shadow-sm bg-white",
                                                            style: "width: 60px; height: 60px; object-fit: cover;"
                                                        })
                                                        : h.div(
                                                            {
                                                                class: "rounded-3 border border-3 border-white shadow-sm d-flex align-items-center justify-content-center fw-bold text-white fs-4",
                                                                style: "width: 60px; height: 60px; background: linear-gradient(135deg, #0d6efd 0%, #0098f0 100%);"
                                                            },
                                                            initial
                                                        )
                                                ),

                                                // Company Name and Details (Stacked on the right of Logo)
                                                h.div(
                                                    {
                                                        class: "text-start p-2 rounded-3 flex-grow-1 shadow-sm",
                                                        style: "background: rgba(255, 255, 255, 0.8); backdrop-filter: blur(8px);"
                                                    },
                                                    h.div(
                                                        { class: "d-flex align-items-center gap-2 mb-1" },
                                                        h.h4({ class: "fw-bold mb-0 text-dark", style: "font-size: 1.15rem; line-height: 1.2;" }, company.name),
                                                        isVerified ? h.i({ class: "bi bi-patch-check-fill text-primary", style: "font-size: 0.95rem;", title: "Verified Business" }) : null
                                                    ),
                                                    h.p({ class: "text-muted mb-0", style: "font-size: 0.75rem; line-height: 1.1;" }, "@", company.handle),
                                                    either(
                                                        formatRating(company.avg_rating),
                                                        () => h.div(
                                                            { class: "d-flex align-items-center gap-1 mt-1" },
                                                            h.i({ class: "bi bi-star text-primary", style: "font-size: 0.7rem;" }),
                                                            h.span({ class: "fw-bold text-dark", style: "font-size: 0.75rem;" }, formatRating(company.avg_rating)),
                                                            company.rating_count
                                                                ? h.span({ class: "text-muted", style: "font-size: 0.75rem;" }, `(${company.rating_count})`)
                                                                : null
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    )
                                ),

                                // Right Column (Only visible from md and above)
                                h.div(
                                    { class: "col-12 col-md-6 d-none d-md-block" },
                                    h.div(
                                        { class: "bg-white p-4 rounded-4 shadow-sm h-100 border border-light d-flex flex-column justify-content-between" },
                                        h.div(
                                            h.h5({ class: "fw-bold text-dark mb-2" }, "About Us"),
                                            h.p({ class: "text-secondary mb-3", style: "font-size: 0.95rem; line-height: 1.5;" }, company.description || "No description available.")
                                        ),
                                        h.div(
                                            // Stats Pills
                                            h.div(
                                                { class: "d-flex gap-3 mb-4" },
                                                h.div(
                                                    { class: "d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-3 border" },
                                                    h.i({ class: "bi bi-box-seam text-primary fs-5" }),
                                                    h.div(
                                                        h.div({ class: "text-muted small fw-bold text-uppercase", style: "font-size: 0.65rem;" }, "Orders"),
                                                        h.span({ class: "fw-bold text-dark fs-6" }, formatStatsWithPlus(company.order_count || 0))
                                                    )
                                                ),
                                                h.div(
                                                    { class: "d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-3 border" },
                                                    h.i({ class: "bi bi-heart text-danger fs-5" }),
                                                    h.div(
                                                        h.div({ class: "text-muted small fw-bold text-uppercase", style: "font-size: 0.65rem;" }, "Favorites"),
                                                        h.span({ class: "fw-bold text-dark fs-6" }, formatStatsWithPlus(company.save_count || 0))
                                                    )
                                                )
                                            ),
                                            // Contact Links
                                            h.h6({ class: "fw-bold text-dark mb-2 small text-uppercase text-muted" }, "Contact Us"),
                                            h.div(
                                                { class: "d-flex gap-2 flex-wrap" },
                                                company.support_phone ? h.a(
                                                    { class: "btn btn-outline-primary btn-sm rounded-pill px-3 d-flex align-items-center gap-2", href: `tel:${company.support_phone}`, target: "_blank" },
                                                    h.i({ class: "bi bi-telephone" }),
                                                    "Call"
                                                ) : null,
                                                company.support_phone ? h.a(
                                                    { class: "btn btn-outline-success btn-sm rounded-pill px-3 d-flex align-items-center gap-2", href: `https://api.whatsapp.com/send?phone=${company.support_phone.replace(/[^0-9]/g, "")}`, target: "_blank" },
                                                    h.i({ class: "bi bi-whatsapp" }),
                                                    "WhatsApp"
                                                ) : null,
                                                company.support_email ? h.a(
                                                    { class: "btn btn-outline-info btn-sm rounded-pill px-3 d-flex align-items-center gap-2", href: `mailto:${company.support_email}`, target: "_blank" },
                                                    h.i({ class: "bi bi-envelope" }),
                                                    "Email"
                                                ) : null
                                            )
                                        )
                                    )
                                )
                            )
                        ),
                        
                        h.div(
                            { class: "container py-4" },

                            // 1.2. Active Deals (only shown while the business has a live deal)
                            v(this.activeDealsState, (dealsState) => {
                                let deals = dealsState.value || [];
                                if (deals.length === 0) return h.div();
                                return this.renderDealsCarousel(deals);
                            }),

                            // 1.5. Featured Products (only shown once the business has featured at least one)
                            v(this.featuredProductsState, (featuredState) => {
                                let products = featuredState.value || [];
                                if (products.length === 0) return h.div();
                                return this.renderProductCarousel(
                                    "Featured Products",
                                    products,
                                    "business.products.featured",
                                );
                            }),

                            // 2.5. Top Sellers (only shown once the business has sales to rank)
                            v(this.topSellersState, (sellersState) => {
                                let products = sellersState.value || [];
                                if (products.length === 0) return h.div();
                                return this.renderProductCarousel(
                                    "Top Sellers",
                                    products,
                                    "business.products.top-sellers",
                                );
                            }),

                            // 3. The Conversion Layer (Products Catalog)
                            h.div(
                                { class: "mb-5" },
                                v(this.categoriesState, (catsState) => {
                                    let categories = catsState.value || [];
                                    if (categories.length === 0) return h.div(); // No categories
                                    
                                    return v(this.selectedCategoryState, (selCatState) => {
                                        let selectedCatId = selCatState.value;

                                        // This row rebuilds on every category
                                        // selection (this v() re-runs), which can
                                        // otherwise leave the newly active pill
                                        // scrolled out of view - e.g. picking one
                                        // near the edge shifts the row and the
                                        // active pill ends up off-screen. Key each
                                        // button on its category id, then once the
                                        // rebuilt row has actually landed in the
                                        // DOM (deferred so this runs after that,
                                        // not before), find the active one by that
                                        // key and scroll it back into view.
                                        setTimeout(() => {
                                            dom
                                                .get(`[data-category-id="${selectedCatId}"]`)
                                                ?.scrollIntoView({
                                                    behavior: "smooth",
                                                    inline: "center",
                                                    block: "nearest",
                                                });
                                        }, 0);

                                        return h.div(
                                            { class: "d-flex overflow-auto flex-nowrap pb-2 mb-4 gap-2", style: "scrollbar-width: none;" },
                                            ...categories.map(cat => h.button(
                                                {
                                                    class: `btn border border-2 px-4 py-2 fw-semibold flex-shrink-0 transition-all ${cat.id === selectedCatId ? "border-primary text-primary" : "btn-light text-secondary"}`,
                                                    "data-category-id": cat.id,
                                                    listeners: {
                                                        click: () => this.selectCategory(cat.id)
                                                    }
                                                },
                                                cat.name
                                            ))
                                        );
                                    });
                                }),
                                
                                v(this.categoryLoading, (loadingVal) => {
                                    let isLoading = loadingVal.value;
                                    if (isLoading) {
                                        return h.div(
                                            { class: "d-flex justify-content-center align-items-center w-100 py-5" },
                                            h.div({ class: "spinner-border text-primary", role: "status" })
                                        );
                                    }

                                    return v(this.subcategoriesState, (subcatsState) => {
                                        let subcategories = subcatsState.value || [];
                                        if (subcategories.length === 0) {
                                            return h.div(
                                                { class: "bg-white p-5 rounded-4 border text-center shadow-sm" },
                                                h.div(
                                                    { class: "rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3 bg-light", style: "width: 80px; height: 80px;" },
                                                    h.i({ class: "bi bi-box-seam text-secondary fs-1" })
                                                ),
                                                h.h5({ class: "fw-bold text-dark" }, "No products available"),
                                                h.p({ class: "text-muted mb-0" }, "We couldn't find any products in this category.")
                                            );
                                        }

                                        return h.div(
                                            ...subcategories.map(subcat => {
                                                let products = subcat.products || [];
                                                if (products.length === 0) return null;
                                                
                                                return h.div(
                                                    { class: "mb-5" },
                                                    h.div(
                                                        { class: "d-flex justify-content-between align-items-center mb-3" },
                                                        h.h5({ class: "fw-bold text-dark m-0" }, subcat.name),
                                                        h.span(
                                                            { 
                                                                class: "btn btn-sm btn-light fw-bold text-primary",
                                                                role: 'button',
                                                                listeners: {
                                                                    click: () => {
                                                                        redirect(`/business/${this.businessId}/subcategory/${subcat.id}/products`)
                                                                    }
                                                                }
                                                            },
                                                            "Browse all ",
                                                            h.i({ class: "bi bi-chevron-right" })
                                                        )
                                                    ),
                                                    h.div(
                                                        { class: "d-flex overflow-auto flex-nowrap gap-3 pb-3 desktop-scrollbar", style: "scrollbar-width: none;" },
                                                        ...products.slice(0, 10).map(product => h.SearchResultProductView(product, {
                                                            class: "flex-shrink-0 h-100",
                                                            style: isMobile() ? "width: 150px;" : "width: 280px;",
                                                            listeners: {
                                                                click: () => {
                                                                    broker.send(
                                                                        app("$e").product.needs.display,
                                                                        payload({ product }),
                                                                    );
                                                                }
                                                            }
                                                        }))
                                                    )
                                                );
                                            })
                                        );
                                    });
                                })
                            )
                        )
                    );
                });
            })
        );
    }
}

ojs(BusinessProfileDefault, SearchResultProductView, OffcanvasBody, ModalBody);

